import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Send } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { Input, Textarea } from '@components/Input';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { invoiceInvalidationService } from '@services/invoiceInvalidationService';
import { Company, formatCnpj } from '@/types/company';
import { INVOICE_MODEL_OPTIONS, INVOICE_MODEL_DEFAULT } from '@/types/invoice';
import { InvoiceInvalidationCreatePayload } from '@/types/invoiceInvalidation';

const currentYearTwoDigits = () => new Date().getFullYear() % 100;

const JUSTIFICATION_MIN = 15;
const JUSTIFICATION_MAX = 255;

export const InvoiceInvalidationFormView: React.FC = () => {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [year, setYear] = useState<number>(currentYearTwoDigits());
  const [model, setModel] = useState<number>(INVOICE_MODEL_DEFAULT);
  const [serie, setSerie] = useState<number>(1);
  const [numberStart, setNumberStart] = useState<number | ''>('');
  const [numberEnd, setNumberEnd] = useState<number | ''>('');
  const [justification, setJustification] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    companyService
      .getCompanies()
      .then((data) => {
        setCompanies(data);
        if (data.length === 1) setCompanyId(data[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  }, []);

  const justificationLength = justification.trim().length;
  const justificationValid =
    justificationLength >= JUSTIFICATION_MIN && justificationLength <= JUSTIFICATION_MAX;

  const rangeCount = useMemo(() => {
    if (numberStart === '' || numberEnd === '') return 0;
    if (Number(numberEnd) < Number(numberStart)) return 0;
    return Number(numberEnd) - Number(numberStart) + 1;
  }, [numberStart, numberEnd]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!companyId) errors.companyId = 'Selecione a empresa';
    if (!Number.isInteger(year) || year < 0 || year > 99) errors.year = 'Ano inválido (00–99)';
    if (model !== 55 && model !== 65) errors.model = 'Modelo deve ser 55 ou 65';
    if (!Number.isInteger(serie) || serie < 0 || serie > 999) errors.serie = 'Série deve estar entre 0 e 999';
    if (numberStart === '' || Number(numberStart) < 1) errors.numberStart = 'Número inicial inválido';
    if (numberEnd === '' || Number(numberEnd) < 1) errors.numberEnd = 'Número final inválido';
    if (numberStart !== '' && numberEnd !== '' && Number(numberEnd) < Number(numberStart)) {
      errors.numberEnd = 'Número final deve ser maior ou igual ao inicial';
    }
    if (!justificationValid) {
      errors.justification = `Justificativa deve ter entre ${JUSTIFICATION_MIN} e ${JUSTIFICATION_MAX} caracteres`;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitClick = () => {
    setError(null);
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (companyId === '' || numberStart === '' || numberEnd === '') return;

    const payload: InvoiceInvalidationCreatePayload = {
      company_id: Number(companyId),
      year,
      model,
      serie,
      number_start: Number(numberStart),
      number_end: Number(numberEnd),
      justification: justification.trim(),
    };

    try {
      setSubmitting(true);
      const created = await invoiceInvalidationService.create(payload);
      navigate(`/settings/fiscal/invoice-invalidations/${created.invalidation_id}`, {
        state: { successMessage: 'Invalidação processada com sucesso.' },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao submeter invalidação');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PermissionGuard
      permission="invoice_invalidation.create"
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para inutilizar numerações.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate('/settings/fiscal/invoice-invalidations')}
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">Nova Invalidação</h1>
            <p className="text-slate-600">
              Submeta uma faixa de numeração de NF-e/NFC-e para inutilização na SEFAZ.
            </p>
          </div>
        </div>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent>
            <div className="flex gap-3">
              <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-amber-900">
                <p className="font-medium">Atenção: a inutilização é uma operação irreversível na SEFAZ.</p>
                <p className="mt-1">
                  Verifique cuidadosamente a empresa, série e faixa de números antes de prosseguir.
                  Para utilizar esta funcionalidade a empresa deve ter o certificado A1 e a senha do certificado
                  cadastrados.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader title="Dados da Invalidação" />
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="md:col-span-6">
                <label className="text-label block mb-1">
                  Empresa <span className="text-red-500">*</span>
                </label>
                <select
                  className="input-field w-full text-sm px-3 py-2"
                  value={companyId === '' ? '' : companyId}
                  onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Selecione...</option>
                  {companies.map((c) => (
                    <option key={c.company_id} value={c.company_id}>
                      {c.name} — {formatCnpj(c.document)}
                    </option>
                  ))}
                </select>
                {fieldErrors.companyId && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.companyId}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <Input
                  label="Ano (AA)"
                  type="number"
                  required
                  min={0}
                  max={99}
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value || 0))}
                  error={fieldErrors.year}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-label block mb-1">
                  Modelo <span className="text-red-500">*</span>
                </label>
                <select
                  className="input-field w-full text-sm px-3 py-2"
                  value={model}
                  onChange={(e) => setModel(Number(e.target.value))}
                >
                  {INVOICE_MODEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.model && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.model}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <Input
                  label="Série"
                  type="number"
                  required
                  min={0}
                  max={999}
                  value={serie}
                  onChange={(e) => setSerie(Number(e.target.value || 0))}
                  error={fieldErrors.serie}
                />
              </div>

              <div className="md:col-span-4">
                <Input
                  label="Número inicial"
                  type="number"
                  required
                  min={1}
                  value={numberStart === '' ? '' : numberStart}
                  onChange={(e) => setNumberStart(e.target.value === '' ? '' : Number(e.target.value))}
                  error={fieldErrors.numberStart}
                />
              </div>
              <div className="md:col-span-4">
                <Input
                  label="Número final"
                  type="number"
                  required
                  min={1}
                  value={numberEnd === '' ? '' : numberEnd}
                  onChange={(e) => setNumberEnd(e.target.value === '' ? '' : Number(e.target.value))}
                  error={fieldErrors.numberEnd}
                />
              </div>
              <div className="md:col-span-4 flex items-end">
                <div className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700 w-full">
                  Quantidade na faixa: <span className="font-semibold">{rangeCount}</span>
                </div>
              </div>

              <div className="md:col-span-12">
                <Textarea
                  label="Justificativa"
                  required
                  rows={3}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value.slice(0, JUSTIFICATION_MAX))}
                  error={fieldErrors.justification}
                  helperText={`${justificationLength}/${JUSTIFICATION_MAX} caracteres (mínimo ${JUSTIFICATION_MIN})`}
                  placeholder="Descreva o motivo da inutilização (mín. 15 caracteres)..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/settings/fiscal/invoice-invalidations')}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitClick}
            loading={submitting}
            icon={<Send size={18} />}
          >
            Submeter à SEFAZ
          </Button>
        </div>

        <ConfirmDialog
          isOpen={confirmOpen}
          title="Confirmar inutilização"
          message={
            `Tem certeza que deseja inutilizar a faixa ${numberStart}–${numberEnd} da série ${serie} ` +
            `(modelo ${model}) da empresa selecionada? Esta ação é IRREVERSÍVEL na SEFAZ.`
          }
          confirmText="Sim, submeter"
          cancelText="Cancelar"
          severity="danger"
          loading={submitting}
          onConfirm={handleConfirm}
          onClose={() => setConfirmOpen(false)}
        />
      </div>
    </PermissionGuard>
  );
};
